/* eslint-disable @typescript-eslint/unified-signatures */
import * as random from 'random-js';

/**
 * 生成一个 [0, max] 区间内的随机整数
 * @param max 最大值, 不能大于 0x20000000000000
 * @description 此函数来自第三方库 [random-js](https://github.com/ckknight/random-js) ([source](https://github.com/ckknight/random-js/blob/master/src/distribution/integer.ts))
 * @example
 * ```js
 * randomInteger(100);  // -> 66
 * ```
 */
export function randomInteger(max: number): number;
/**
 * 生成一个 [min, max] 区间内的随机整数
 * @param min 最小值, 不能小于 -0x20000000000000
 * @param max 最大值, 不能大于 0x20000000000000
 * @description 此函数来自第三方库 [random-js](https://github.com/ckknight/random-js) ([source](https://github.com/ckknight/random-js/blob/master/src/distribution/integer.ts)
 * @example
 * ```js
 * randomInteger(200, 1000);  // -> 233
 * ```)
 */
export function randomInteger(min: number, max: number): number;
export function randomInteger(bound: number, max?: number) {
  if (typeof max === 'number') return random.integer(bound, max)(random.nativeMath);
  return random.integer(0, bound)(random.nativeMath);
}

/**
 * 生成一个 [0, max] 区间内的随机浮点数
 * @description 此函数来自第三方库 [random-js](https://github.com/ckknight/random-js) ([source](https://github.com/ckknight/random-js/blob/master/src/distribution/real.ts)
 * @example
 * ```js
 * randomReal(100);  // -> 66.66
 * ```)
 */
export function randomReal(max: number): number;
/**
 * 生成一个 [min, max] 区间内的随机浮点数
 * @description 此函数来自第三方库 [random-js](https://github.com/ckknight/random-js) ([source](https://github.com/ckknight/random-js/blob/master/src/distribution/real.ts)
 * @example
 * ```js
 * randomReal(200, 1000);  // -> 233.33
 * ```))
 */
export function randomReal(min: number, max: number): number;
export function randomReal(bound: number, max?: number) {
  if (typeof max === 'number') return random.real(bound, max, true)(random.nativeMath);
  return random.real(0, bound, true)(random.nativeMath);
}

/**
 * 生成一个随机字符串
 * @param length 要生成的字符串长度, 默认为 16
 * @param pool 字符池, 生成的字符串中的字符都是从这个池子中取出的, 默认为 [A-Za-z0-9_-]
 * @description 此函数来自第三方库 [random-js](https://github.com/ckknight/random-js) ([source](https://github.com/ckknight/random-js/blob/master/src/distribution/string.ts))
 * @example
 * ```js
 * randomString(12);          // -> "8ab_N4oRcT-i"
 * randomString("abc");       // -> "babababbccacabaa"
 * randomString(8, "abc");    // -> "bbccbbba"
 * ```
 */
export function randomString(length?: number): string;
export function randomString(pool?: string): string;
export function randomString(length: number, pool?: string): string;
export function randomString(lengthOrPool: number | string = 16, pool?: string) {
  if (typeof lengthOrPool === 'string') return random.string(lengthOrPool)(random.nativeMath, 16);

  const distribution = typeof pool === 'string' ? random.string(pool) : random.string();
  return distribution(random.nativeMath, lengthOrPool);
}

/**
 * 随机从数组从返回一个元素
 * @param array 要选择的数组, 支持任意 ArrayLike 对象
 * @param begin 要选择的起始位置, 结果中会包含该位置的元素, 默认为 0
 * @param end 要选择的结束位置, 结果中**不会**包含该位置的元素, 默认为数组长度
 * @description 此函数来自第三方库 [random-js](https://github.com/ckknight/random-js) ([source](https://github.com/ckknight/random-js/blob/master/src/distribution/pick.ts))
 * @example
 * ```js
 * randomlyPick([2, 4, 8, 16]);                 // -> 4
 * //            ~~~~~~~~~~~
 * randomlyPick([2, 4, 8, 16], 2);              // -> 16
 * //                  ~~~~~
 * randomlyPick([2, 4, 8, 16], 1, 3);           // -> 8
 * //               ~~~~
 * randomlyPick([2, 4, 8, 16], undefined, 2);   // -> 2
 * //            ~~~~
 * randomlyPick([2, 4, 8, 16], 2, 2);           // Error
 * ```
 */
export function randomlyPick<T>(array: ArrayLike<T>, begin?: number, end?: number): T {
  return random.pick(random.nativeMath, array, begin, end);
}

/**
 * 生成一个符合 UUID v4 标准的字符串
 * @description 此函数来自第三方库 [random-js](https://github.com/ckknight/random-js) ([source](https://github.com/ckknight/random-js/blob/master/src/distribution/uuid4.ts))
 * @example
 * ```js
 * uuid();  // -> "0c3fbd2e-d52d-4d52-b292-bb29c9d98319"
 * ```
 */
export function uuid(): string {
  return random.uuid4(random.nativeMath);
}
